// ============================================================================
//  Dashboard.jsx — one man's game plan: the 5F tiles and his goal list.
//
//  Used for both "my dashboard" and the read-only drill-in from the roster;
//  `readOnly` is the only thing that differs.
// ============================================================================

import React, { useState, useCallback } from "react";
import { Plus, ArrowLeft, X, Camera } from "lucide-react";
import { C, FS, display, kicker, primaryBtn, smallGhost } from "../theme/tokens.js";
import { Mark, Ring, Bar, Avatar } from "../theme/primitives.jsx";
import { monthKey, personGoals, goalLatest, fProgress, overall } from "../lib/metrics.js";
import {
  upsertGoal, deleteGoal as deleteGoalRow, upsertMark,
  addPhoto as addPhotoRow, deletePhoto as deletePhotoRow,
} from "../lib/storage.js";
import MarkModal from "../components/MarkModal.jsx";
import GoalModal from "../components/GoalModal.jsx";
import GoalCard from "../components/GoalCard.jsx";

export default function Dashboard({ st, setSt, personId, readOnly, onBack, onEditProfile }) {
  const person = st.people[personId];
  const [activeF, setActiveF] = useState("all");
  const [markGoal, setMarkGoal] = useState(null);
  const [goalModal, setGoalModal] = useState(null);
  const goals = personGoals(st, personId);
  const ov = overall(st, personId);

  const commitMark = useCallback(async (gid, data) => {
    const mk = monthKey();
    await upsertMark(gid, { month: mk, ...data });
    setSt((prev) => {
      const n = structuredClone(prev);
      const arr = n.marks[gid] || [];
      const i = arr.findIndex((x) => x.month === mk);
      const e = { month: mk, ts: Date.now(), ...data };
      if (i >= 0) arr[i] = e; else arr.push(e);
      n.marks[gid] = arr; return n;
    });
    setMarkGoal(null);
  }, [setSt]);

  const commitGoal = useCallback(async (payload, ex) => {
    const id = await upsertGoal(personId, ex ? { ...ex, ...payload } : payload);
    setSt((prev) => {
      const n = structuredClone(prev);
      const list = n.goals[personId] || [];
      if (ex) {
        const i = list.findIndex((g) => g.id === ex.id);
        if (i >= 0) list[i] = { ...list[i], ...payload };
      } else {
        list.push({ id, photos: [], ...payload });
      }
      n.goals[personId] = list; return n;
    });
    setGoalModal(null);
  }, [setSt, personId]);

  const removeGoal = useCallback(async (gid) => {
    await deleteGoalRow(gid);
    setSt((prev) => {
      const n = structuredClone(prev);
      n.goals[personId] = (n.goals[personId] || []).filter((g) => g.id !== gid);
      delete n.marks[gid]; return n;
    });
  }, [setSt, personId]);

  const addPhoto = useCallback(async (gid, file) => {
    const photo = await addPhotoRow(personId, gid, file);
    setSt((prev) => {
      const n = structuredClone(prev);
      const g = (n.goals[personId] || []).find((x) => x.id === gid);
      if (g) { g.photos = g.photos || []; g.photos.push(photo); }
      return n;
    });
  }, [setSt, personId]);

  const deletePhoto = useCallback(async (gid, pid) => {
    const g = (st.goals[personId] || []).find((x) => x.id === gid);
    const ph = (g?.photos || []).find((p) => p.id === pid);
    await deletePhotoRow(pid, ph?.storage_path);
    setSt((prev) => {
      const n = structuredClone(prev);
      const g2 = (n.goals[personId] || []).find((x) => x.id === gid);
      if (g2) g2.photos = (g2.photos || []).filter((p) => p.id !== pid);
      return n;
    });
  }, [setSt, personId, st]);

  const visible = activeF === "all" ? goals : goals.filter((g) => g.f === activeF);

  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "0 24px 80px" }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between",
        flexWrap: "wrap", gap: 20, padding: "32px 0 26px" }}>
        <div style={{ display: "flex", gap: 18, alignItems: "center" }}>
          <div>
            {onBack && <button onClick={onBack} style={{ ...smallGhost, marginBottom: 12, paddingLeft: 0 }}><ArrowLeft size={14} /> Back</button>}
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <Avatar person={person} size={56} />
              <div>
                <div style={kicker}>{person.retreat} · Game Plan</div>
                <h1 style={{ ...display, fontSize: 34, margin: "6px 0 0", color: C.text, lineHeight: 1 }}>
                  {person.name}
                </h1>
                <div style={{ fontSize: 12.5, color: C.sub, marginTop: 5, letterSpacing: ".02em" }}>
                  {readOnly ? "Mile Markers" : "Your Mile Markers"}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {!readOnly && (
            <button onClick={onEditProfile} style={smallGhost}><Camera size={14} /> Edit profile</button>
          )}
          <div style={{ textAlign: "right" }}>
            <div style={kicker}>Overall</div>
            <div style={{ ...display, fontSize: 30, color: C.red, marginTop: 4 }}>{ov}%</div>
          </div>
          <div style={{ position: "relative" }}>
            <Ring value={ov} />
            <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}><Mark size={26} /></div>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 12, marginBottom: 24 }}>
        {FS.map((f) => {
          const p = fProgress(st, personId, f.key); const Ic = f.icon;
          const on = activeF === f.key;
          const count = goals.filter((g) => g.f === f.key).length;
          return (
            <button key={f.key} onClick={() => setActiveF(on ? "all" : f.key)} title={`Show ${f.label} goals`}
              style={{
                textAlign: "left", cursor: "pointer", padding: "16px 16px 18px", borderRadius: 14,
                border: on ? `2px solid ${C.red}` : `1px solid ${C.line}`,
                background: on ? "#FFF5F7" : C.panel,
                boxShadow: on ? "0 4px 16px rgba(213,0,50,0.16)" : "0 1px 2px rgba(19,34,41,0.04)",
                transform: on ? "translateY(-2px)" : "none",
                transition: "all 180ms" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Ic size={17} color={on ? C.red : C.sub} />
                <span style={{ ...display, fontSize: 19, color: p === null ? C.faint : (on ? C.red : C.text) }}>
                  {p === null ? "—" : `${p}%`}
                </span>
              </div>
              <div style={{ ...display, fontSize: 16, marginTop: 12, color: C.text }}>{f.label}</div>
              <div style={{ fontSize: 11.5, color: C.faint, marginTop: 3 }}>{f.blurb}</div>
              <div style={{ marginTop: 10 }}><Bar value={p ?? 0} /></div>
              <div style={{ fontSize: 10.5, color: on ? C.red : C.faint, marginTop: 9,
                letterSpacing: ".04em", fontWeight: 600 }}>
                {on ? "✓ SHOWING THIS F" : `TAP TO VIEW · ${count} GOAL${count !== 1 ? "S" : ""}`}
              </div>
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {activeF === "all" ? (
            <span style={{ fontSize: 13, color: C.sub }}>
              All goals <span style={{ color: C.faint }}>· {visible.length} across all 5 F's</span>
            </span>
          ) : (
            <>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8,
                background: C.redSoft, color: C.redDk, padding: "5px 12px 5px 13px",
                borderRadius: 99, fontSize: 12.5, fontWeight: 700, border: `1px solid ${C.redLine}` }}>
                {FS.find((x) => x.key === activeF)?.label}
                <span style={{ color: C.red, opacity: 0.7 }}>
                  · {fProgress(st, personId, activeF) ?? 0}%
                </span>
                <button onClick={() => setActiveF("all")} title="Clear filter"
                  style={{ border: "none", background: "transparent", color: C.redDk,
                    cursor: "pointer", display: "grid", placeItems: "center", padding: 0,
                    marginLeft: 2 }}>
                  <X size={13} />
                </button>
              </span>
              <span style={{ fontSize: 12.5, color: C.faint }}>
                {visible.length} goal{visible.length !== 1 ? "s" : ""} in this F
              </span>
            </>
          )}
        </div>
        {!readOnly && (
          <button onClick={() => setGoalModal({ fKey: activeF === "all" ? "faith" : activeF })} style={primaryBtn}>
            <Plus size={16} /> Add SMART goal
          </button>
        )}
      </div>

      {visible.length === 0 ? (
        <div style={{ textAlign: "center", padding: "56px 20px", border: `1.5px dashed ${C.lineHi}`,
          borderRadius: 16, color: C.sub, background: C.panel }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}><Mark size={34} /></div>
          <div style={{ ...display, fontSize: 18, color: C.text }}>No goals here yet</div>
          <p style={{ fontSize: 13.5, margin: "8px auto 0", maxWidth: 360, lineHeight: 1.6 }}>
            {readOnly ? "This man hasn't entered goals for this area yet."
              : "Add the SMART goals you set at the retreat. 2–3 per F is the rhythm."}
          </p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 14 }}>
          {visible.map((g) => (
            <GoalCard key={g.id} goal={g} mark={goalLatest(st, g.id)} history={st.marks[g.id] || []}
              readOnly={readOnly} onMark={() => setMarkGoal(g)}
              onEdit={() => setGoalModal({ existing: g })} onDelete={() => removeGoal(g.id)}
              onAddPhoto={addPhoto} onDeletePhoto={deletePhoto} />
          ))}
        </div>
      )}

      {markGoal && (
        <MarkModal goal={markGoal}
          existing={(st.marks[markGoal.id] || []).find((x) => x.month === monthKey())}
          onSave={(d) => commitMark(markGoal.id, d)} onClose={() => setMarkGoal(null)} />
      )}
      {goalModal && (
        <GoalModal fKey={goalModal.fKey} existing={goalModal.existing}
          onSave={(p) => commitGoal(p, goalModal.existing)} onClose={() => setGoalModal(null)} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  TEAM DASHBOARD — roster grid                                       */
/* ------------------------------------------------------------------ */
